import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/soul";
import { useAuth } from "@/context/AuthContext";
import { VerseReflection } from "@/components/VerseReflection";
import { ShareButton } from "@/components/ShareButton";
import { FeltCheck } from "@/components/FeltCheck";
import { downloadVerseCard } from "@/lib/verseCard";
import { toast } from "sonner";
import { ImageDown } from "lucide-react";

export default function RedLetters() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const isPaid = user && (user.tier === "seeker" || user.tier === "contributor");
  const navigateToPricing = () => {
    toast.info("Saving verse cards is part of the Seeker path.");
    navigate("/pricing");
  };
  const [verses, setVerses] = useState([]);
  const [daily, setDaily] = useState(null);
  const [notes, setNotes] = useState({});

  const loadNotes = useCallback(() => {
    if (!user) {
      setNotes({});
      return;
    }
    api.get("/reflections").then((r) => {
      const map = {};
      // list is newest-first; keep the first (latest) per verse_ref
      r.data.forEach((rf) => {
        if (!(rf.verse_ref in map)) map[rf.verse_ref] = rf.note;
      });
      setNotes(map);
    }).catch(() => {});
  }, [user]);

  useEffect(() => {
    api.get("/red-letters").then((r) => setVerses(r.data.verses));
    api.get("/red-letters/daily").then((r) => setDaily(r.data.verse)).catch(() => {});
  }, []);

  useEffect(() => {
    loadNotes();
  }, [loadNotes]);

  const onSaved = (ref, note) => setNotes((prev) => ({ ...prev, [ref]: note }));

  return (
    <main
      data-testid="red-letters-page"
      className="min-h-screen"
      style={{
        background:
          "radial-gradient(1200px 600px at 80% -10%, rgba(139,0,0,0.25), transparent 60%)",
      }}
    >
      <div className="max-w-5xl mx-auto px-6 py-16">
        <div className="max-w-3xl mb-14 fade-up">
          <p className="text-[#C1272D] tracking-[0.3em] uppercase text-xs mb-4">The Red Letters</p>
          <h1 className="serif text-4xl sm:text-6xl font-light text-[#F5F1E7]">
            The words of <span className="italic-serif text-[#C1272D]">Christ.</span>
          </h1>
          <p className="mt-5 text-[#A8A29E] leading-relaxed text-lg">
            His walk of life pointed always to one place — the kingdom of heaven
            within. Sit with each word. Let it read you.
          </p>
        </div>

        {daily && (
          <div
            data-testid="red-daily"
            className="glass rounded-2xl p-8 sm:p-12 mb-12 border"
            style={{ borderColor: "rgba(193,39,45,0.35)" }}
          >
            <p className="text-xs uppercase tracking-[0.25em] text-[#A8A29E] mb-4">Today's word</p>
            <blockquote className="italic-serif text-3xl sm:text-4xl leading-snug text-[#C1272D]">
              “{daily.text}”
            </blockquote>
            <p className="mt-4 text-[#D4AF37] tracking-wide">— {daily.reference}</p>
            <p className="mt-4 text-[#A8A29E] leading-relaxed max-w-2xl">{daily.reflection}</p>
            <div className="mt-5">
              <ShareButton
                testId="share-daily-verse"
                title={`${daily.reference} — the words of Christ`}
                text={`“${daily.text}”\n— ${daily.reference} (KJV)\n\nShared from Mirrors of Your Soul`}
                label="Share today's word"
              />
            </div>
            <FeltCheck testId="felt-daily" sourcePrompt={`Reading “${daily.text}” — ${daily.reference}`} />
          </div>
        )}

        <div className="space-y-6" data-testid="red-verses">
          {verses.map((v, i) => (
            <div
              key={i}
              data-testid={`red-verse-${i}`}
              className="glass rounded-2xl p-8 border-l-2 hover:border-l-[#C1272D] transition-colors duration-500"
              style={{ borderLeftColor: "rgba(193,39,45,0.5)" }}
            >
              <blockquote className="italic-serif text-2xl sm:text-3xl leading-snug text-[#C1272D]">
                “{v.text}”
              </blockquote>
              <p className="mt-3 text-[#D4AF37] tracking-wide text-sm">— {v.reference}</p>
              <p className="mt-4 text-[#A8A29E] leading-relaxed">{v.reflection}</p>
              <div className="mt-4">
                <ShareButton
                  testId={`share-verse-${i}`}
                  title={`${v.reference} — the words of Christ`}
                  text={`“${v.text}”\n— ${v.reference} (KJV)\n\nShared from Mirrors of Your Soul`}
                  label="Share this verse"
                />
                <button
                  data-testid={`card-verse-${i}`}
                  onClick={() => isPaid ? downloadVerseCard({ text: v.text, attribution: `${v.reference} (KJV)`, filename: `red-letter-${i}` }) : navigateToPricing()}
                  className="ml-3 inline-flex items-center gap-2 rounded-full border border-white/15 text-[#A8A29E] px-5 py-2 text-sm hover:text-[#D4AF37] hover:border-[#D4AF37]/50 transition-colors duration-300"
                >
                  <ImageDown size={15} /> Save as card
                </button>
              </div>
              <VerseReflection verse={v} savedNote={notes[v.reference]} onSaved={onSaved} />
              <FeltCheck testId={`felt-verse-${i}`} sourcePrompt={`Reading “${v.text}” — ${v.reference}`} />
            </div>
          ))}
        </div>
      </div>
    </main>
  );
}
